/**
 * EyeControl Input Hook
 * Translates EyeControl device gestures (sent as keyboard events) to scanning actions
 * Based on user's action mapping configuration
 */

import { useEffect, useCallback, useRef } from 'react';
import { getUserSettings } from '@/lib/userSettings';
import { useInputMethod } from '@/contexts/InputMethodContext';
import { InputMethod } from '@/lib/inputMethods';
import { dispatchScanningAction, ScanningAction } from '@/lib/scanningSystem';

// Mapping from gesture IDs to keyboard keys that EyeControl device sends
const GESTURE_TO_KEY_MAP: Record<string, { key: string; code: string }> = {
  look_up: { key: 'ArrowUp', code: 'ArrowUp' },
  look_down: { key: 'ArrowDown', code: 'ArrowDown' },
  look_left: { key: 'ArrowLeft', code: 'ArrowLeft' },
  look_right: { key: 'ArrowRight', code: 'ArrowRight' },
  blink: { key: 'Enter', code: 'Enter' },
};

// Reverse mapping: from key to gesture
function getGestureFromKey(e: KeyboardEvent): string | null {
  for (const [gestureId, keyInfo] of Object.entries(GESTURE_TO_KEY_MAP)) {
    if (e.key === keyInfo.key || e.code === keyInfo.code) {
      return gestureId;
    }
  }
  return null;
}

/**
 * Get the action mapped to a gesture based on user's action mapping
 */
export function getEyeControlAction(e: KeyboardEvent): ScanningAction | null {
  const settings = getUserSettings();
  const actionMapping = settings.actionMapping || {};
  
  // Get which gesture this key represents
  const gesture = getGestureFromKey(e);
  if (!gesture) return null;
  
  // Find which action is mapped to this gesture
  for (const [actionId, mappedGesture] of Object.entries(actionMapping)) {
    if (mappedGesture === gesture) {
      // Convert action ID to ScanningAction
      // EyeControl uses 'previous' but ScanningSystem uses 'prev'
      if (actionId === 'previous') return 'prev';
      return actionId as ScanningAction;
    }
  }
  
  return null;
}

interface UseEyeControlInputProps {
  enabled?: boolean;
}

/**
 * Hook that listens for EyeControl device input and dispatches scanning actions
 * This hook intercepts keyboard events BEFORE the default handlers when EyeControl is active
 */
export function useEyeControlInput({ enabled = true }: UseEyeControlInputProps = {}) {
  const { inputMethod } = useInputMethod();
  
  const isEyeControl = inputMethod === InputMethod.EYE_CONTROL;
  
  // Prevent keyup from triggering native button clicks when we intercepted keydown.
  // In Chrome, Enter on a focused <button> fires click on keyup, not keydown.
  const suppressedKeysRef = useRef<Set<string>>(new Set());

  const handleKeyUp = useCallback((e: KeyboardEvent) => {
    if (suppressedKeysRef.current.has(e.key)) {
      suppressedKeysRef.current.delete(e.key);
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  }, []);

  // Wrap handleKeyDown to track suppressed keys
  const handleKeyDownWrapped = useCallback((e: KeyboardEvent) => {
    const tag = (e.target as HTMLElement)?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) return;
    if (!enabled || !isEyeControl) return;

    const settings = getUserSettings();
    const inputCount = settings.eyeControlGestures?.length || 1;
    const isAutomaticForced = inputCount <= 2;

    let action = getEyeControlAction(e);

    const isEnterOrSpace = e.key === 'Enter' || e.key === ' ';
    if (isAutomaticForced && isEnterOrSpace) {
      if (action !== 'cancel' && action !== 'back') {
        action = 'select';
      }
    }

    if (!action) return;

    // Track this key so we also suppress keyup (prevents native button click)
    suppressedKeysRef.current.add(e.key);

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    dispatchScanningAction(action);
  }, [enabled, isEyeControl]);

  useEffect(() => {
    if (!enabled || !isEyeControl) return;
    
    // Use capture phase with highest priority to intercept before other handlers
    window.addEventListener('keydown', handleKeyDownWrapped, { capture: true });
    window.addEventListener('keyup', handleKeyUp, { capture: true });
    return () => {
      window.removeEventListener('keydown', handleKeyDownWrapped, { capture: true });
      window.removeEventListener('keyup', handleKeyUp, { capture: true });
    };
  }, [enabled, isEyeControl, handleKeyDownWrapped, handleKeyUp]);
  
  return { isEyeControl };
}
